import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { csvCell } from '../../../lib/csvCell';
import { ShieldAlert, Plus, Search, X, UserCheck, Ban, Eye, ChevronLeft, ChevronRight, Download, Upload, FileText, CheckCircle2, AlertTriangle } from 'lucide-react';
import { safeFetch } from '../../../lib/api';
import { notifyToast } from '../../../lib/toastStore';

export interface RedlistEntry {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  reason: string;
  addedBy?: string;
  addedAt?: string;
  status: 'active' | 'inactive';
  deactivatedBy?: string;
  deactivatedAt?: string;
  deactivationReason?: string;
}

const PAGE_SIZE = 25;

/**
 * Splits a CSV name into the stored entry shape: firstName is the FIRST
 * name only, the surname goes in lastName. A single "Name" column mapped
 * to First Name carries the combined name, so it is split on whitespace
 * when no separate Last Name value exists. Mirrors the bulk-import
 * handler's normalisation so client classification and server storage
 * can never drift apart.
 */
function splitImportName(rawFirst: string, rawLast: string): { firstName: string; lastName: string } {
  const first = String(rawFirst || '').trim();
  const last = String(rawLast || '').trim();
  if (!last && /\s/.test(first)) {
    const parts = first.split(/\s+/);
    return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
  }
  return { firstName: first, lastName: last };
}

/**
 * Canonical phone dedupe key — mirrors the server matcher's
 * canonicalPhoneFingerprintToken: digits only, with an 11-digit leading
 * '1' (NANP country code) dropped, so format variants of the same
 * number dedupe together.
 */
function phoneDedupeKey(raw: string): string {
  const digits = String(raw || '').replace(/\D/g, '');
  return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
}

interface ApplicantHit {
  uid: string;
  displayName: string;
  firstName: string;
  email: string;
  phone: string;
  applicationId: string;
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 min-h-[44px] text-sm text-slate-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100';
const labelCls = 'block text-xs font-bold text-slate-600 mb-1.5';

/**
 * Redlist management — senior-admin-only section on the Workflow page.
 * Discrete but visible: add entries manually or fast-add from current applicants.
 */
function parseCsvContent(text: string): { headers: string[]; rows: Record<string, string>[] } {
  const lines = text.split(/\r?\n/).filter((line) => line.trim().length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };

  const parseLine = (line: string): string[] => {
    const result: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(cur.trim());
        cur = '';
      } else {
        cur += char;
      }
    }
    result.push(cur.trim());
    return result;
  };

  const headers = parseLine(lines[0]);
  const rows: Record<string, string>[] = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseLine(lines[i]);
    const rowObj: Record<string, string> = {};
    headers.forEach((h, idx) => {
      rowObj[h] = values[idx] || '';
    });
    rows.push(rowObj);
  }

  return { headers, rows };
}

export function RedlistSection() {
  const [entries, setEntries] = useState<RedlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [mode, setMode] = useState<'fast' | 'manual'>('fast');

  // Bulk Import state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importStep, setImportStep] = useState<1 | 2 | 3>(1);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [fieldMapping, setFieldMapping] = useState({ firstName: '', lastName: '', email: '', phone: '', reason: '' });
  const [defaultReason, setDefaultReason] = useState('Bulk imported redlist entry');
  const [importing, setImporting] = useState(false);

  // Manual form
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  // Shared reason
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Fast add
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<ApplicantHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<ApplicantHit | null>(null);

  // Deactivate
  const [deactivating, setDeactivating] = useState<RedlistEntry | null>(null);
  const [deactReason, setDeactReason] = useState('');
  const [deactBusy, setDeactBusy] = useState(false);

  // Table: search / status filter / pagination / detail view
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'active' | 'all'>('active');
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<RedlistEntry | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setForbidden(false);
    try {
      const qs = statusFilter === 'all' ? '?includeInactive=true' : '';
      const data = await safeFetch(`/api/admin/workflow/redlist${qs}`);
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
    } catch (err: any) {
      if (String(err.message || '').includes('403') || String(err.message || '').toLowerCase().includes('forbidden')) {
        setForbidden(true);
      } else {
        notifyToast('error', err.message || 'Failed to load redlist');
      }
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return entries;
    return entries.filter((e) =>
      [e.firstName, e.email, e.phone, e.reason, e.addedBy]
        .some((v) => String(v || '').toLowerCase().includes(q))
    );
  }, [entries, search]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageEntries = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => { setPage(1); }, [search, statusFilter]);

  // Debounced applicant search
  useEffect(() => {
    if (mode !== 'fast' || query.trim().length < 2) { setHits([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const data = await safeFetch(`/api/admin/workflow/redlist/search-applicants?q=${encodeURIComponent(query.trim())}`);
        setHits(Array.isArray(data?.results) ? data.results : []);
      } catch {
        setHits([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [query, mode]);

  const mappedCandidates = useMemo(() => {
    if (!csvRows.length) return [];

    const existingEmails = new Set(
      entries.filter((e) => e.status === 'active' && e.email).map((e) => e.email!.toLowerCase().trim())
    );
    const existingPhones = new Set(
      entries.filter((e) => e.status === 'active' && e.phone).map((e) => phoneDedupeKey(e.phone!)).filter(Boolean)
    );
    const existingNames = new Set(
      entries.filter((e) => e.status === 'active' && e.firstName).map((e) => e.firstName!.toLowerCase().trim())
    );

    const seenInBatchEmails = new Set<string>();
    const seenInBatchPhones = new Set<string>();
    const seenInBatchNames = new Set<string>();

    return csvRows.map((row, idx) => {
      // Stored shape: firstName is the first name only, surname separate —
      // the dedupe name key below is that first name, matching how manual
      // entries are stored and how the server dedupes.
      const { firstName: fn, lastName: ln } = splitImportName(
        fieldMapping.firstName ? row[fieldMapping.firstName] || '' : '',
        fieldMapping.lastName ? row[fieldMapping.lastName] || '' : ''
      );
      const email = (fieldMapping.email ? row[fieldMapping.email] || '' : '').toLowerCase().trim();
      const phone = (fieldMapping.phone ? row[fieldMapping.phone] || '' : '').trim();
      const phoneKey = phoneDedupeKey(phone);
      const reasonRaw = (fieldMapping.reason ? row[fieldMapping.reason] || '' : '').trim();
      const reason = reasonRaw || defaultReason.trim() || 'Bulk imported redlist entry';

      if (!fn && !ln && !email && !phone) {
        return { rowIdx: idx + 1, firstName: fn, lastName: ln, email, phone, reason, status: 'invalid', message: 'Missing name, email and phone' };
      }

      const isEmailDup = !!email && (existingEmails.has(email) || seenInBatchEmails.has(email));
      const isPhoneDup = !!phoneKey && (existingPhones.has(phoneKey) || seenInBatchPhones.has(phoneKey));
      const isNameDup = !email && !phoneKey && !!fn && (existingNames.has(fn.toLowerCase()) || seenInBatchNames.has(fn.toLowerCase()));

      if (isEmailDup || isPhoneDup || isNameDup) {
        return { rowIdx: idx + 1, firstName: fn, lastName: ln, email, phone, reason, status: 'duplicate', message: isEmailDup ? 'Email already on redlist' : isPhoneDup ? 'Phone already on redlist' : 'Name already on redlist' };
      }

      if (email) seenInBatchEmails.add(email);
      if (phoneKey) seenInBatchPhones.add(phoneKey);
      if (fn) seenInBatchNames.add(fn.toLowerCase());

      return { rowIdx: idx + 1, firstName: fn, lastName: ln, email, phone, reason, status: 'valid', message: 'Ready to import' };
    });
  }, [csvRows, fieldMapping, defaultReason, entries]);

  const validEntries = mappedCandidates.filter((c) => c.status === 'valid');
  const duplicateEntries = mappedCandidates.filter((c) => c.status === 'duplicate');
  const invalidEntries = mappedCandidates.filter((c) => c.status === 'invalid');

  const handleExecuteImport = async () => {
    if (validEntries.length === 0) {
      notifyToast('error', 'No valid non-duplicate records to import.');
      return;
    }
    setImporting(true);
    try {
      const payload = validEntries.map((item) => ({
        firstName: item.firstName,
        lastName: item.lastName,
        email: item.email,
        phone: item.phone,
        reason: item.reason,
      }));

      const res = await safeFetch('/api/admin/workflow/redlist/bulk-import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entries: payload }),
      });
      notifyToast('success', `Successfully imported ${res.imported || validEntries.length} redlist entries (${res.duplicatesSkipped || 0} duplicates skipped).`);
      setShowImportModal(false);
      load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Bulk import failed.');
    } finally {
      setImporting(false);
    }
  };

  const autoDetectMapping = (headers: string[]) => {
    const findMatch = (candidates: string[]) => {
      return headers.find((h) => candidates.some((c) => h.toLowerCase().includes(c))) || '';
    };
    setFieldMapping({
      firstName: findMatch(['first', 'fn', 'given', 'name']),
      lastName: findMatch(['last', 'ln', 'surname', 'family']),
      email: findMatch(['email', 'mail']),
      phone: findMatch(['phone', 'tel', 'mobile', 'cell']),
      reason: findMatch(['reason', 'note', 'comment', 'cause', 'description']),
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseCsvContent(text);
      if (parsed.headers.length === 0) {
        notifyToast('error', 'CSV file appears to be empty.');
        return;
      }
      setCsvHeaders(parsed.headers);
      setCsvRows(parsed.rows);
      autoDetectMapping(parsed.headers);
      setImportStep(2);
    };
    reader.readAsText(file);
  };

  const resetForm = () => {
    setFirstName(''); setEmail(''); setPhone(''); setReason('');
    setQuery(''); setHits([]); setSelected(null); setShowForm(false);
  };

  const handleAdd = async () => {
    const r = reason.trim();
    if (r.length < 10) { notifyToast('error', 'Reason must be at least 10 characters.'); return; }
    let payload: any;
    if (mode === 'fast') {
      if (!selected) { notifyToast('error', 'Select an applicant first.'); return; }
      payload = { firstName: selected.firstName, email: selected.email, phone: selected.phone, reason: r };
    } else {
      if (!firstName.trim() && !email.trim() && !phone.trim()) {
        notifyToast('error', 'At least one identifier (name, email, or phone) is required.'); return;
      }
      payload = { firstName: firstName.trim(), email: email.trim(), phone: phone.trim(), reason: r };
    }
    setSaving(true);
    try {
      await safeFetch('/api/admin/workflow/redlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      notifyToast('success', 'Added to redlist.');
      resetForm();
      load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to add entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivating) return;
    setDeactBusy(true);
    try {
      await safeFetch(`/api/admin/workflow/redlist/${encodeURIComponent(deactivating.id)}/deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: deactReason.trim() }),
      });
      notifyToast('success', 'Entry deactivated.');
      setDeactivating(null); setDeactReason('');
      load();
    } catch (err: any) {
      notifyToast('error', err.message || 'Failed to deactivate');
    } finally {
      setDeactBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="p-2 rounded-xl bg-red-100 text-red-700"><ShieldAlert className="h-5 w-5" /></span>
          <div>
            <h2 className="text-base font-extrabold text-slate-900">Redlist</h2>
            <p className="text-xs text-slate-500">{entries.filter((e) => e.status === 'active').length} active {entries.filter((e) => e.status === 'active').length === 1 ? 'entry' : 'entries'} — barred or restricted contacts</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              if (entries.length === 0) { notifyToast('error', 'No entries to export.'); return; }
              const headers = ['First Name', 'Email', 'Phone', 'Reason', 'Status', 'Added At', 'Added By'];
              const csvData = [headers.join(',')];
              entries.forEach((e) => {
                csvData.push([
                  e.firstName || '',
                  e.email || '',
                  e.phone || '',
                  e.reason || '',
                  e.status || '',
                  e.addedAt || '',
                  e.addedBy || '',
                ].map(csvCell).join(','));
              });
              const blob = new Blob([csvData.join('\n')], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `redlist_export_${Date.now()}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              notifyToast('success', 'Redlist exported as CSV');
            }}
            className="px-3 py-2 min-h-[44px] rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <Download className="h-3.5 w-3.5 text-slate-500" /> Export CSV
          </button>
          <button
            type="button"
            onClick={() => {
              setShowImportModal(true);
              setImportStep(1);
              setCsvHeaders([]);
              setCsvRows([]);
              setFieldMapping({ firstName: '', lastName: '', email: '', phone: '', reason: '' });
              setDefaultReason('Bulk imported redlist entry');
            }}
            className="px-3 py-2 min-h-[44px] rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-red-800 text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            <Upload className="h-3.5 w-3.5 text-red-600" /> Bulk Import CSV
          </button>
          <button
            type="button"
            onClick={() => { setShowForm(!showForm); setMode('fast'); }}
            className="px-3 py-2 min-h-[44px] rounded-xl bg-red-700 hover:bg-red-600 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer"
          >
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {showForm ? 'Close' : 'Add Entry'}
          </button>
        </div>
      </div>

      {/* Add form */}
      {showForm && (
        <div className="bg-white border border-red-200 rounded-2xl p-4 sm:p-5 space-y-4 shadow-sm">
          {/* Mode toggle */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1 w-fit">
            {([['fast', 'From Applicant'], ['manual', 'Manual Entry']] as const).map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                aria-pressed={mode === m}
                className={`px-4 py-2 rounded-lg text-xs font-extrabold cursor-pointer transition-colors ${
                  mode === m ? 'bg-brand text-white shadow-xs' : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {mode === 'fast' ? (
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Search current applicants</label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
                    placeholder="Name, email, or application ID…"
                    className={`${inputCls} pl-10`}
                    autoFocus
                  />
                </div>
              </div>
              {searching && <p className="text-xs text-slate-500">Searching…</p>}
              {!searching && hits.length > 0 && !selected && (
                <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100">
                  {hits.map((h) => (
                    <button
                      key={h.uid}
                      type="button"
                      onClick={() => setSelected(h)}
                      className="w-full text-left px-3 py-2.5 hover:bg-red-50 cursor-pointer flex items-center gap-3"
                    >
                      <span className="p-1.5 rounded-lg bg-slate-100 text-slate-500"><UserCheck className="h-4 w-4" /></span>
                      <span className="min-w-0">
                        <span className="block text-sm font-bold text-slate-800 truncate">{h.displayName || '—'}</span>
                        <span className="block text-xs text-slate-500 truncate">{h.email}{h.applicationId ? ` · ${h.applicationId}` : ''}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {!searching && query.trim().length >= 2 && hits.length === 0 && (
                <p className="text-xs text-slate-500">No applicants found.</p>
              )}
              {selected && (
                <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
                  <span className="p-1.5 rounded-lg bg-red-100 text-red-700"><UserCheck className="h-4 w-4" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-800 truncate">{selected.displayName}</p>
                    <p className="text-xs text-slate-500 truncate">{selected.email}{selected.phone ? ` · ${selected.phone}` : ''}</p>
                  </div>
                  <button type="button" onClick={() => { setSelected(null); setQuery(''); }} className="p-2 text-slate-400 hover:text-slate-700 cursor-pointer" aria-label="Clear selection">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>First name</label>
                <input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="e.g. John" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e.g. john@example.com" className={inputCls} inputMode="email" />
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. +18765550100" className={inputCls} inputMode="tel" />
              </div>
            </div>
          )}

          <div>
            <label className={labelCls}>Reason <span className="text-red-600">*</span> <span className="font-medium text-slate-400">(min 10 characters)</span></label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this contact being redlisted?"
              rows={3}
              className={`${inputCls} resize-y`}
            />
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleAdd}
              disabled={saving}
              className="px-4 py-2.5 min-h-[44px] rounded-xl bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-bold cursor-pointer"
            >
              {saving ? 'Adding…' : 'Add to Redlist'}
            </button>
            <button type="button" onClick={resetForm} className="px-4 py-2.5 min-h-[44px] rounded-xl text-slate-500 hover:text-slate-800 text-sm font-bold cursor-pointer">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Entries table */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-400">Loading redlist…</div>
      ) : forbidden ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <ShieldAlert className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm font-bold text-slate-600">Senior admin access required</p>
          <p className="text-xs text-slate-500 mt-1">Only senior administrators can view and manage the redlist.</p>
        </div>
      ) : entries.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center">
          <ShieldAlert className="h-8 w-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm font-bold text-slate-600">{statusFilter === 'all' ? 'No redlist entries' : 'No active redlist entries'}</p>
          <p className="text-xs text-slate-500 mt-1">Contacts added here will flag the redlist pre-vetting check.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {/* Toolbar: search + status filter */}
          <div className="flex flex-wrap items-center gap-2 px-3 py-2.5 border-b border-slate-100">
            <div className="relative flex-1 min-w-[200px] max-w-sm">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email, phone, reason…"
                className="w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 py-2 text-xs text-slate-800 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100"
              />
            </div>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 ml-auto">
              {([['active', 'Active'], ['all', 'All incl. inactive']] as const).map(([f, label]) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setStatusFilter(f)}
                  aria-pressed={statusFilter === f}
                  className={`px-3 py-1.5 rounded-md text-[11px] font-extrabold cursor-pointer transition-colors ${
                    statusFilter === f ? 'bg-brand text-white shadow-xs' : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <p className="px-4 py-8 text-center text-xs text-slate-500">No entries match “{search}”.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-[11px] font-extrabold uppercase tracking-wide text-slate-500">
                      <th className="px-3 py-2">Name</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">Phone</th>
                      <th className="px-3 py-2">Reason</th>
                      <th className="px-3 py-2 whitespace-nowrap">Added</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pageEntries.map((e) => (
                      <tr
                        key={e.id}
                        onClick={() => setDetail(e)}
                        className="hover:bg-red-50/50 cursor-pointer"
                        title="View details"
                      >
                        <td className="px-3 py-1.5 font-bold text-slate-800 whitespace-nowrap max-w-[160px] truncate">{e.firstName || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-600 max-w-[200px] truncate">{e.email || '—'}</td>
                        <td className="px-3 py-1.5 text-slate-600 whitespace-nowrap">{e.phone || '—'}</td>
                        <td className="px-3 py-1.5 text-xs text-slate-500 max-w-[260px] truncate" title={e.reason}>{e.reason}</td>
                        <td className="px-3 py-1.5 text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(e.addedAt)}</td>
                        <td className="px-3 py-1.5">
                          {e.status === 'active' ? (
                            <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide bg-red-100 text-red-700 rounded-full px-2 py-0.5">
                              <Ban className="h-3 w-3" /> Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">
                              Inactive
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-1.5">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              onClick={(ev) => { ev.stopPropagation(); setDetail(e); }}
                              className="px-2.5 py-1.5 min-h-[36px] rounded-lg border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300 text-[11px] font-bold cursor-pointer inline-flex items-center gap-1"
                            >
                              <Eye className="h-3.5 w-3.5" /> View
                            </button>
                            {e.status === 'active' && (
                              <button
                                type="button"
                                onClick={(ev) => { ev.stopPropagation(); setDeactivating(e); }}
                                className="px-2.5 py-1.5 min-h-[36px] rounded-lg border border-slate-200 text-slate-500 hover:text-red-700 hover:border-red-300 text-[11px] font-bold cursor-pointer"
                              >
                                Deactivate
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-t border-slate-100 text-[11px] text-slate-500">
                <span>
                  Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
                  {search.trim() ? ` matching “${search.trim()}”` : ''}
                </span>
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage(safePage - 1)}
                    className="p-1.5 min-h-[36px] min-w-[36px] rounded-lg border border-slate-200 text-slate-500 hover:text-slate-800 disabled:opacity-40 cursor-pointer inline-flex items-center justify-center"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="font-bold text-slate-600">Page {safePage} of {pageCount}</span>
                  <button
                    type="button"
                    disabled={safePage >= pageCount}
                    onClick={() => setPage(safePage + 1)}
                    className="p-1.5 min-h-[36px] min-w-[36px] rounded-lg border border-slate-200 text-slate-500 hover:text-slate-800 disabled:opacity-40 cursor-pointer inline-flex items-center justify-center"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setDetail(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start gap-3">
              <span className="p-2 rounded-xl bg-red-100 text-red-700 shrink-0"><ShieldAlert className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-extrabold text-slate-900 truncate">{detail.firstName || detail.email || detail.phone || 'Redlist entry'}</h3>
                <p className="text-[11px] text-slate-500">Redlist entry · {detail.id}</p>
              </div>
              {detail.status === 'active' ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-wide bg-red-100 text-red-700 rounded-full px-2.5 py-1 shrink-0">
                  <Ban className="h-3 w-3" /> Active
                </span>
              ) : (
                <span className="inline-flex items-center text-[10px] font-extrabold uppercase tracking-wide bg-slate-100 text-slate-500 rounded-full px-2.5 py-1 shrink-0">
                  Inactive
                </span>
              )}
              <button type="button" onClick={() => setDetail(null)} className="p-1.5 text-slate-400 hover:text-slate-700 cursor-pointer shrink-0" aria-label="Close details">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div>
              <h4 className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1.5">Contact</h4>
              <dl className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-slate-50 rounded-lg px-2.5 py-2">
                  <dt className="text-slate-400 font-bold">Name</dt>
                  <dd className="font-bold text-slate-800 mt-0.5 break-words">{detail.firstName || '—'}</dd>
                </div>
                <div className="bg-slate-50 rounded-lg px-2.5 py-2">
                  <dt className="text-slate-400 font-bold">Email</dt>
                  <dd className="font-bold text-slate-800 mt-0.5 break-words">{detail.email || '—'}</dd>
                </div>
                <div className="bg-slate-50 rounded-lg px-2.5 py-2">
                  <dt className="text-slate-400 font-bold">Phone</dt>
                  <dd className="font-bold text-slate-800 mt-0.5 break-words">{detail.phone || '—'}</dd>
                </div>
              </dl>
            </div>

            <div>
              <h4 className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1.5">Reason</h4>
              <p className="text-sm text-slate-700 whitespace-pre-wrap bg-red-50/60 border border-red-100 rounded-xl px-3 py-2.5">{detail.reason}</p>
            </div>

            <div>
              <h4 className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400 mb-1.5">Audit</h4>
              <dl className="space-y-1.5 text-xs text-slate-600">
                <div className="flex gap-2">
                  <dt className="w-32 shrink-0 font-bold text-slate-400">Added</dt>
                  <dd>{fmtDateTime(detail.addedAt)}{detail.addedBy ? ` · by ${detail.addedBy}` : ''}</dd>
                </div>
                {detail.status !== 'active' && (
                  <>
                    <div className="flex gap-2">
                      <dt className="w-32 shrink-0 font-bold text-slate-400">Deactivated</dt>
                      <dd>{fmtDateTime(detail.deactivatedAt)}{detail.deactivatedBy ? ` · by ${detail.deactivatedBy}` : ''}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="w-32 shrink-0 font-bold text-slate-400">Deactivation reason</dt>
                      <dd className="whitespace-pre-wrap">{detail.deactivationReason || '—'}</dd>
                    </div>
                  </>
                )}
              </dl>
            </div>

            <div className="flex items-center gap-2 justify-end border-t border-slate-100 pt-3">
              <button type="button" onClick={() => setDetail(null)} className="px-4 py-2.5 min-h-[44px] rounded-xl text-slate-500 hover:text-slate-800 text-sm font-bold cursor-pointer">
                Close
              </button>
              {detail.status === 'active' && (
                <button
                  type="button"
                  onClick={() => { const e = detail; setDetail(null); setDeactivating(e); }}
                  className="px-4 py-2.5 min-h-[44px] rounded-xl bg-red-700 hover:bg-red-600 text-white text-sm font-bold cursor-pointer"
                >
                  Deactivate
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Deactivate modal */}
      {deactivating && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/50" onClick={() => setDeactivating(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 space-y-4">
            <h3 className="text-sm font-extrabold text-slate-900">Deactivate redlist entry</h3>
            <p className="text-xs text-slate-500">
              Removing <span className="font-bold text-slate-700">{deactivating.firstName || deactivating.email || deactivating.phone}</span> from the active redlist.
              This is audit-logged.
            </p>
            <div>
              <label className={labelCls}>Reason (optional)</label>
              <textarea
                value={deactReason}
                onChange={(e) => setDeactReason(e.target.value)}
                placeholder="Why is this entry being deactivated?"
                rows={2}
                className={`${inputCls} resize-y`}
              />
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button type="button" onClick={() => setDeactivating(null)} className="px-4 py-2.5 min-h-[44px] rounded-xl text-slate-500 hover:text-slate-800 text-sm font-bold cursor-pointer">
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeactivate}
                disabled={deactBusy}
                className="px-4 py-2.5 min-h-[44px] rounded-xl bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-sm font-bold cursor-pointer"
              >
                {deactBusy ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-[9500] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xs" onClick={() => setShowImportModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6 space-y-5 max-h-[90vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2.5">
                <span className="p-2 rounded-xl bg-red-100 text-red-700"><Upload className="h-5 w-5" /></span>
                <div>
                  <h3 className="text-base font-extrabold text-slate-900">Bulk Import Redlist CSV</h3>
                  <p className="text-xs text-slate-500">Step {importStep} of 3 — {importStep === 1 ? 'Select CSV File' : importStep === 2 ? 'Map CSV Columns' : 'Validate & Execute Import'}</p>
                </div>
              </div>
              <button aria-label="Close" title="Close" type="button" onClick={() => setShowImportModal(false)} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg cursor-pointer">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Step 1: File Selection */}
            {importStep === 1 && (
              <div className="space-y-4 py-4">
                <div className="border-2 border-dashed border-slate-200 hover:border-red-400 rounded-2xl p-8 text-center bg-slate-50 hover:bg-red-50/40 transition-colors">
                  <FileText className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                  <p className="text-sm font-extrabold text-slate-800">Select a CSV File</p>
                  <p className="text-xs text-slate-500 mt-1 mb-4">File should contain headers for First Name, Last Name, Email, Phone, and Reason.</p>
                  <label className="px-5 py-2.5 bg-red-700 hover:bg-red-600 text-white rounded-xl text-xs font-extrabold cursor-pointer inline-flex items-center gap-2 shadow-xs">
                    <Upload className="h-4 w-4" /> Browse CSV File
                    <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
                  </label>
                </div>
              </div>
            )}

            {/* Step 2: Column Mapping */}
            {importStep === 2 && (
              <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                <p className="text-xs text-slate-600 font-medium">Map CSV headers to required Redlist entry fields:</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>First Name Column</label>
                    <select
                      value={fieldMapping.firstName}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, firstName: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Last Name Column</label>
                    <select
                      value={fieldMapping.lastName}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, lastName: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">-- Select Column (Optional) --</option>
                      {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Email Column</label>
                    <select
                      value={fieldMapping.email}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, email: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">-- Select Column --</option>
                      {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Phone Column</label>
                    <select
                      value={fieldMapping.phone}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, phone: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">-- Select Column (Optional) --</option>
                      {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>Reason Column</label>
                    <select
                      value={fieldMapping.reason}
                      onChange={(e) => setFieldMapping({ ...fieldMapping, reason: e.target.value })}
                      className={inputCls}
                    >
                      <option value="">-- Select Column (Optional) --</option>
                      {csvHeaders.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Default Reason Text (used if CSV reason is unmapped or empty)</label>
                  <input
                    type="text"
                    value={defaultReason}
                    onChange={(e) => setDefaultReason(e.target.value)}
                    placeholder="e.g. Bulk imported redlist entry"
                    className={inputCls}
                  />
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <button type="button" onClick={() => setImportStep(1)} className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer">
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setImportStep(3)}
                    className="px-5 py-2.5 rounded-xl bg-red-700 hover:bg-red-600 text-white text-xs font-extrabold cursor-pointer"
                  >
                    Validate &amp; Preview Import
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Validation & Execution */}
            {importStep === 3 && (
              <div className="space-y-4 overflow-y-auto flex-1 pr-1">
                {/* Summary Strip */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
                    <span className="text-2xl font-extrabold text-emerald-700 tabular-nums">{validEntries.length}</span>
                    <p className="text-[10px] font-extrabold uppercase text-emerald-800">Ready to Import</p>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                    <span className="text-2xl font-extrabold text-amber-700 tabular-nums">{duplicateEntries.length}</span>
                    <p className="text-[10px] font-extrabold uppercase text-amber-800">Skipped Duplicates</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-center">
                    <span className="text-2xl font-extrabold text-slate-600 tabular-nums">{invalidEntries.length}</span>
                    <p className="text-[10px] font-extrabold uppercase text-slate-600">Invalid Rows</p>
                  </div>
                </div>

                {/* Preview Table */}
                <div className="border border-slate-200 rounded-xl overflow-hidden max-h-52 overflow-y-auto">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-50 sticky top-0 text-[10px] font-extrabold uppercase text-slate-500 border-b border-slate-200">
                      <tr>
                        <th className="px-3 py-2">Row</th>
                        <th className="px-3 py-2">Name</th>
                        <th className="px-3 py-2">Email</th>
                        <th className="px-3 py-2">Reason</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {mappedCandidates.slice(0, 50).map((item, i) => (
                        <tr key={i} className={item.status === 'valid' ? 'bg-white' : item.status === 'duplicate' ? 'bg-amber-50/50' : 'bg-slate-50'}>
                          <td className="px-3 py-1.5 font-mono text-[11px] text-slate-500">#{item.rowIdx}</td>
                          <td className="px-3 py-1.5 font-bold text-slate-800">{item.firstName} {item.lastName}</td>
                          <td className="px-3 py-1.5 text-slate-600">{item.email || '—'}</td>
                          <td className="px-3 py-1.5 text-slate-500 max-w-[150px] truncate">{item.reason}</td>
                          <td className="px-3 py-1.5">
                            {item.status === 'valid' ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                                <CheckCircle2 className="h-3 w-3" /> Ready
                              </span>
                            ) : item.status === 'duplicate' ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full" title={item.message}>
                                <AlertTriangle className="h-3 w-3" /> Duplicate
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[11px] font-extrabold uppercase bg-slate-200 text-slate-700 px-2 py-0.5 rounded-full">
                                Invalid
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <button type="button" onClick={() => setImportStep(2)} className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer">
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleExecuteImport}
                    disabled={importing || validEntries.length === 0}
                    className="px-5 py-2.5 rounded-xl bg-red-700 hover:bg-red-600 disabled:opacity-50 text-white text-xs font-extrabold cursor-pointer flex items-center gap-1.5 shadow-xs"
                  >
                    {importing ? 'Executing Import…' : `Confirm & Execute Import (${validEntries.length})`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
